import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import LanguageSelector from "../LanguageSelector/LanguageSelector";
import "./Reports.css";

// Production backend fallback matching Cloudflare / Render configuration
const PROD_BACKEND_URL = "https://sgp-backend-osuk.onrender.com";
const BACKEND = (process.env.REACT_APP_API_URL || PROD_BACKEND_URL).replace(/\/$/, "");

function Reports() {
  const navigate = useNavigate();
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    issueType: "",
    message: "",
    comment: ""
  });

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (rating === 0 && !formData.issueType && !formData.message && !formData.comment) {
      setStatus({
        type: "error",
        text: "Please select an issue type, describe your issue, or give a star rating before submitting."
      });
      return;
    }

    setLoading(true);
    setStatus(null);

    try {
      let signal;
      try {
        signal = AbortSignal.timeout(10000);
      } catch (ignore) {
        const controller = new AbortController();
        setTimeout(() => controller.abort(), 10000);
        signal = controller.signal;
      }

      const response = await fetch(`${BACKEND}/api/send-report`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.name.trim() || "Anonymous",
          email: formData.email.trim() || "",
          issueType: formData.issueType || "",
          message: formData.message.trim() || "",
          comment: formData.comment.trim() || "",
          rating: rating || 0
        }),
        signal
      });

      const data = await response.json().catch(() => null);

      if (response.ok) {
        setStatus({
          type: "success",
          text: data?.message || "Report submitted successfully. A confirmation/notification email has been sent."
        });
        setFormData({ name: "", email: "", issueType: "", message: "", comment: "" });
        setRating(0);
      } else {
        const errorDetail =
          response.status === 429
            ? "Too many reports submitted. Please wait a few minutes before trying again."
            : response.status === 503
            ? "Unable to send the report right now. Please try again later."
            : (data?.error && !data.error.toLowerCase().includes("smtp") && !data.error.toLowerCase().includes("pass") && !data.error.toLowerCase().includes("key")
                ? data.error
                : "Unable to send the report right now. Please try again later.");
        throw new Error(errorDetail);
      }
    } catch (err) {
      console.error("Report submission failed:", err);
      let errorMsg = err?.message || "Unable to send the report right now. Please try again later.";
      if (
        err.name === "TimeoutError" ||
        err.name === "AbortError" ||
        errorMsg.toLowerCase().includes("aborted") ||
        errorMsg.toLowerCase().includes("timeout")
      ) {
        errorMsg = "Email service is taking too long to respond. Please try again later.";
      } else if (err.name === "TypeError" || errorMsg.includes("Failed to fetch") || errorMsg.includes("NetworkError")) {
        errorMsg = `Unable to connect to the backend server (${BACKEND}). Please check your internet connection or verify the server is running.`;
      }
      setStatus({ type: "error", text: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  const ratingLabels = ["", "Poor", "Fair", "Good", "Very Good", "Excellent"];

  return (
    <div className="reports-page">
      <header className="reports-header">
        <div className="reports-header-brand">
          <h1>Reports &amp; Feedback</h1>
          <p>Report issues and share your scholarship portal experience</p>
        </div>
        <div className="reports-header-actions">
          <button className="reports-back-btn" onClick={() => navigate("/dashboard")}>
            Back To Dashboard
          </button>
          <LanguageSelector />
        </div>
      </header>

      <div className="reports-card">
        {/* Header */}
        <div className="card-header">
          <div className="header-icons">
            <span>🛠</span>
            <span>⭐</span>
          </div>
          <h1>Help &amp; Feedback</h1>
          <p>Report a problem or share your experience with the scholarship portal.</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="divider"><span>Report an Issue</span></div>

          {/* Name + Email */}
          <div className="form-row">
            <div className="field-group">
              <label>Name <span className="req">*</span></label>
              <input
                type="text"
                name="name"
                placeholder="Your full name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>
            <div className="field-group">
              <label>Email <span className="req">*</span></label>
              <input
                type="email"
                name="email"
                placeholder="your@email.com"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          {/* Issue Type */}
          <div className="field-group">
            <label>Issue Type <span className="opt">(optional)</span></label>
            <select name="issueType" value={formData.issueType} onChange={handleChange}>
              <option value="">Select issue type</option>
              <option>Document Upload Issue</option>
              <option>Verification Error</option>
              <option>Portal Not Working</option>
              <option>Eligibility Question</option>
              <option>Other</option>
            </select>
          </div>

          {/* Description */}
          <div className="field-group">
            <label>Description <span className="opt">(optional)</span></label>
            <textarea
              name="message"
              placeholder="Describe the issue in detail..."
              value={formData.message}
              onChange={handleChange}
            />
          </div>

          <div className="divider"><span>Write a Review</span></div>

          {/* Rating + Comments */}
          <div className="form-row rating-row">
            <div className="field-group rating-group">
              <label>Your Rating <span className="opt">(optional)</span></label>
              <div className="star-row">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    type="button"
                    key={star}
                    className={`star-btn ${star <= (hoveredRating || rating) ? "filled" : ""}`}
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHoveredRating(star)}
                    onMouseLeave={() => setHoveredRating(0)}
                  >
                    ★
                  </button>
                ))}
              </div>
              {(hoveredRating || rating) > 0 && (
                <span className="rating-label">{ratingLabels[hoveredRating || rating]}</span>
              )}
              <div className="rating-badge-row">
                {[1, 2, 3, 4, 5].map((n) => (
                  <div
                    key={n}
                    className={`rating-badge ${rating >= n ? "active" : ""}`}
                    onClick={() => setRating(n)}
                  >
                    {n}
                  </div>
                ))}
              </div>
            </div>

            <div className="field-group">
              <label>Comments <span className="opt">(optional)</span></label>
              <textarea
                name="comment"
                placeholder="Share your experience with the portal..."
                value={formData.comment}
                onChange={handleChange}
                className="comment-area"
              />
            </div>
          </div>

          {/* Submit */}
          <button type="submit" className="submit-btn" disabled={loading}>
            {loading ? <span className="spinner" /> : "Submit"}
          </button>

          {status && (
            <p className={`status-msg ${status.type}`}>
              {status.type === "success" ? "✅" : "❌"} {status.text}
            </p>
          )}
        </form>
      </div>
    </div>
  );
}

export default Reports;
